import React, { useState, useEffect } from 'react'
import { useApi } from '../../context/apiContext'
import {
  Column,
  Table,
  AutoSizer,
  SortDirection,
  InfiniteLoader,
  ColumnSizer,
} from 'react-virtualized'
import 'react-virtualized/styles.css'
import { format } from 'date-fns'
import { FaFileDownload, FaChevronUp, FaChevronDown } from 'react-icons/fa'
import Breadcrumbs from './Breadcrumbs'
import FunnelFilter from './FunnelFilter'
import Description from './Description'
import SideDrawer from '../Download/SideDrawer'
import { getTokenByAppName } from '../../utils/common-utils'

const FinderComponent = ({ token }) => {
  const [files, setFiles] = useState([])
  const [path, setPath] = useState(null)
  const [breadcrumbs, setBreadcrumbs] = useState([])
  const [offset, setOffset] = useState(0)
  const { apiType, apiClient } = useApi()
  const [searchText, setSearchText] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({ type: '' })
  const [sortBy, setSortBy] = useState([
    { field: 'type', direction: SortDirection.DESC },
    { field: 'name', direction: SortDirection.ASC }, // Default sort by 'name' ascending
  ])
  const [hasMore, setHasMore] = useState(true)
  const [filterByKind, setFilterByKind] = useState('All')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [fileName, setFileName] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [sizeInGB, setSizeInGB] = useState(0)
  const [instructionUrl, setInstructionUrl] = useState('')
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true)
  const [isResultsExpanded, setIsResultsExpanded] = useState(true)

  const limit = 50
  const maxRecords = 1000
  let isFetching = false
  const ROW_HEIGHTS = {
    COMPACT: 48,
    DEFAULT: 60,
    COMFORTABLE: 72,
  }

  const getRowHeight = ({ index }) => {
    const rowData = files[index] || {}
    const hasLongName = rowData.name && rowData.name.length > 50
    const hasLongPath =
      rowData.parentPath && rowData.parentPath.split('/').length > 3

    if (hasLongName || hasLongPath) {
      return ROW_HEIGHTS.COMFORTABLE
    }

    return window.innerWidth <= 768 ? ROW_HEIGHTS.COMPACT : ROW_HEIGHTS.DEFAULT
  }

  const handleFilterChange = (selectedKind) => {
    if (selectedKind === 'All') {
      setFilters({ type: '' })
    } else {
      setFilters({ type: selectedKind.toLowerCase() })
    }
    setFilterByKind(selectedKind)
  }

  const toggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded)
  }

  const toggleResults = () => {
    setIsResultsExpanded(!isResultsExpanded)
  }

  useEffect(() => {
    const fetchFiles = async () => {
      if (isFetching) return
      isFetching = true
      try {
        const requestData = {
          search: searchQuery,
          type: filters.type,
          parent_path: path,
          sort_by: sortBy.map((sort) => sort.field).join(','),
          sort_direction: sortBy.map((sort) => sort.direction).join(','),
          offset: offset,
          limit: limit,
        }
        // Pass token to API calls if provided
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        // console.log('Token before API call:', token)
        const response = await apiClient.postRequest(
          '/files/search',
          requestData,
          apiType,
          headers
        )
        const newTotalFetched =
          offset === 0 ? response.length : files.length + response.length
        if (newTotalFetched >= maxRecords) {
          setHasMore(false)
        }
        setFiles((prevFiles) =>
          offset === 0 ? response : [...prevFiles, ...response]
        )
      } catch (error) {
        console.error('Error fetching files:', error)
      } finally {
        isFetching = false
      }
    }
    fetchFiles()
  }, [searchQuery, path, sortBy, filters, offset, token])

  useEffect(() => {
    setFiles([])
    setOffset(0)
    setHasMore(true)
  }, [searchQuery, path, filters, sortBy])

  const isRowLoaded = ({ rowIndex }) => {
    return !!files[rowIndex]
  }

  const loadMoreRows = () => {
    if (files.length >= maxRecords) {
      setHasMore(false)
    } else {
      setOffset((prevOffset) => prevOffset + limit)
    }
    return Promise.resolve()
  }

  const handleSearch = (query) => {
    setBreadcrumbs([])
    setPath(null)
    setSearchQuery(query)
    setOffset(0)
    setHasMore(true)
  }

  const rowGetter = ({ index }) => {
    return files[index] || {}
  }

  const handleRowClick = (rowData) => {
    if (rowData.type === 'folder') {
      // Update breadcrumb for folders
      const path = rowData.parentPath + '/' + rowData.name
      setPath(path)
      setBreadcrumbs(() => path.split('/'))
      setSearchText('')
      setSearchQuery('')
    }
  }

  const handleBreadcrumbClick = (index) => {
    if (index == 0) return
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1)
    setBreadcrumbs(newBreadcrumbs)
    setPath(newBreadcrumbs.join('/'))
    setSearchText('')
    setSearchQuery('')
  }

  const handleSort = (dataKey) => {
    const updatedSort = sortBy.find((sort) => sort.field === dataKey)
      ? sortBy.map((sort) =>
          sort.field === dataKey
            ? {
                ...sort,
                direction:
                  sort.direction === SortDirection.ASC
                    ? SortDirection.DESC
                    : SortDirection.ASC,
              }
            : sort
        )
      : [...sortBy, { field: dataKey, direction: SortDirection.ASC }]

    setSortBy(updatedSort)
  }

  const headerRenderer = ({ dataKey, label }) => {
    const sortInfo = sortBy.find((sort) => sort.field === dataKey)
    const direction = sortInfo ? sortInfo.direction : null

    return (
      <div className="dataset-header">
        <div className="dataset-header-label">
          <span>{label}</span>
          {direction && (
            <span className="sort-icon">
              {direction === SortDirection.ASC ? '▲' : '▼'}
            </span>
          )}
        </div>

        {dataKey === 'type' && (
          <FunnelFilter
            options={['All', 'Folder', 'File']}
            selectedOption={filterByKind}
            onFilterChange={handleFilterChange}
          />
        )}
      </div>
    )
  }

  const nameCellRenderer = ({ cellData, rowData }) => (
    <div>
      <div className="dataset-cell">{cellData}</div>
      {/* Footnote or additional info */}
      <div className="dataset-footnote">
        {rowData.parentPath.split('/').slice(5).join('/') || rowData.name}
      </div>
    </div>
  )

  const downloadFile = (rowData) => {
    const token = getTokenByAppName(rowData.path)
    const fileUrl =
      process.env.REACT_APP_TDIS_DATA_CATALOG_URL_BASE + rowData.path + token
      const instructionUrl =
      process.env.REACT_APP_TDIS_DATA_CATALOG_URL_BASE +
      '/rbfs/InstructionsForStorageExplorerDownload.pdf'+
      token
    const fileName = rowData.name
    const sizeInGB = rowData.fileMetadata.size / 1024 ** 3
    if (sizeInGB > 10) {
      setFileName(fileName)
      setFileUrl(fileUrl)
      setSizeInGB(sizeInGB)
      setInstructionUrl(instructionUrl)
      setIsDrawerOpen(true)
    } else {
      handleBrowserDownload(fileUrl, fileName)
    }
  }

  const typeCellRenderer = ({ rowData }) => {
    if (rowData.type === 'file') {
      return (
        <div
          className="dataset-file-cell"
          onClick={() => downloadFile(rowData)}
        >
          <span>File</span>
          <FaFileDownload className="filedownload-icon" />
        </div>
      )
    }
    return <span>Folder</span>
  }

  const handleBrowserDownload = (fileUrl, fileName) => {
    const anchor = document.createElement('a')
    anchor.target = '_parent'
    
    // Parse URL to handle query strings
    const url = new URL(fileUrl)
    const pathParts = url.pathname.split('/')
    const encodedFileName = encodeURIComponent(fileName)
    
    // Replace the last part of pathname with encoded filename
    pathParts[pathParts.length - 1] = encodedFileName
    url.pathname = pathParts.join('/')
    
    anchor.href = url.toString()
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    setIsDrawerOpen(false)
  }

  const formatSize = (size) => {
    if (!size) return '-'
    const units = ['B', 'KB', 'MB', 'GB']
    const index = Math.floor(Math.log(size) / Math.log(1024))
    return `${(size / Math.pow(1024, index)).toFixed(2)} ${units[index]}`
  }

  const formatDate = (dateString) => {
    return dateString ? format(new Date(dateString), 'MMM dd, yyyy HH:mm') : '-'
  }

  return (
    <div className="search-container-1">
      <div className="content-card finder-content">
        <div className="content-card">
          <div className="card-header" onClick={toggleDescription}>
            <span className="card-title">Description</span>
            <div className="accordion-controls">
              <button
                className={`toggle-btn ${
                  isDescriptionExpanded ? 'expanded' : 'collapsed'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleDescription()
                }}
                aria-label={
                  isDescriptionExpanded
                    ? 'Collapse description'
                    : 'Expand description'
                }
              >
                {isDescriptionExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </button>
            </div>
          </div>
          <div
            className={`card-body ${
              isDescriptionExpanded ? 'expanded' : 'collapsed'
            }`}
          >
            <Description />
          </div>
        </div>
        {/* Results Card */}
        <div className="content-card finder-content">
          <div className="card-header" onClick={toggleResults}>
            <span className="card-title">Datasets</span>
            <div className="accordion-controls">
              <button
                className={`toggle-btn ${
                  isResultsExpanded ? 'expanded' : 'collapsed'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleResults()
                }}
                aria-label={
                  isResultsExpanded ? 'Collapse results' : 'Expand results'
                }
              >
                {isResultsExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </button>
            </div>
          </div>
          <div
            className={`card-body ${
              isResultsExpanded ? 'expanded' : 'collapsed'
            }`}
          >
            <div className="search-bar">
              <input
                type="text"
                placeholder="Type a keyword and click enter..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchText)}
                className="dataset-search-input"
              />
            </div>
            <div className="dataset-table-container">
              <AutoSizer>
                {({ height, width }) => (
                  <ColumnSizer columnCount={4} width={width}>
                    {({ adjustedWidth }) => {
                      const columnWidths = [
                        adjustedWidth * 0.65,
                        adjustedWidth * 0.1,
                        adjustedWidth * 0.1,
                        adjustedWidth * 0.15,
                      ]
                      return (
                        <InfiniteLoader
                          isRowLoaded={isRowLoaded}
                          loadMoreRows={loadMoreRows}
                          rowCount={hasMore ? files.length + 1 : files.length}
                        >
                          {({ onRowsRendered, registerChild }) => (
                            <Table
                              ref={registerChild}
                              onRowsRendered={onRowsRendered}
                              width={width}
                              height={height}
                              headerHeight={60}
                              rowHeight={getRowHeight}
                              rowCount={files.length}
                              rowGetter={rowGetter}
                              onRowClick={({ rowData }) =>
                                handleRowClick(rowData)
                              }
                              sort={({ sortBy }) => handleSort(sortBy)}
                              className="finder-table"
                              rowClassName={({ index }) => {
                                const rowData = files[index] || {}
                                return `${
                                  rowData.type === 'folder'
                                    ? 'folder-row'
                                    : 'file-row'
                                } ${rowData.isActive ? 'active' : ''}`
                              }}
                            >
                              <Column
                                label="Name"
                                dataKey="name"
                                width={columnWidths[0]}
                                cellRenderer={nameCellRenderer}
                                headerRenderer={headerRenderer}
                                style={{ paddingLeft: 0 }}
                              />
                              <Column
                                label="Kind"
                                dataKey="type"
                                width={columnWidths[1]}
                                cellRenderer={typeCellRenderer}
                                headerRenderer={headerRenderer}
                                style={{ paddingLeft: 0 }}
                              />
                              <Column
                                label="Size"
                                dataKey="size"
                                width={columnWidths[2]}
                                cellRenderer={({ rowData }) =>
                                  rowData.fileMetadata?.size
                                    ? formatSize(rowData.fileMetadata.size)
                                    : '-'
                                }
                                headerRenderer={headerRenderer}
                                style={{ paddingLeft: 0 }}
                              />
                              <Column
                                label="Last Modified"
                                dataKey="last_modified"
                                width={columnWidths[3]}
                                cellRenderer={({ rowData }) =>
                                  rowData.fileMetadata?.last_modified
                                    ? formatDate(
                                        rowData.fileMetadata.last_modified
                                      )
                                    : '-'
                                }
                                headerRenderer={headerRenderer}
                                style={{ paddingLeft: 0 }}
                              />
                            </Table>
                          )}
                        </InfiniteLoader>
                      )
                    }}
                  </ColumnSizer>
                )}
              </AutoSizer>
              <SideDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                fileName={fileName}
                sizeInGB={sizeInGB}
                fileUrl={fileUrl}
                instructionUrl={instructionUrl}
                handleBrowserDownload={() =>
                  handleBrowserDownload(fileUrl, fileName)
                }
              />
            </div>
          </div>
        </div>
      </div>
      {breadcrumbs.length > 0 && (
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="breadcrumb-separator">/</span>}
              <button
                className="breadcrumb-button"
                onClick={() => handleBreadcrumbClick(index)}
              >
                {crumb}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export default FinderComponent
